/**
 * How an account's details are written on screen.
 *
 * Here rather than in the components that print them, because the detail
 * screen, the tables and (one day) an export all have to write the same figure
 * the same way, and a second copy of a format is a second format.
 */

/** A bare day, told apart from an instant by having no time on it. */
const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * "1 Jul 2026" — the shape every date in the panel is printed in, from
 * whichever shape the API sent it in.
 *
 * Both are in play and they are not the same thing. `joined_on` is a day,
 * `last_active` is an instant, and `new Date` reads a bare `YYYY-MM-DD` as
 * midnight UTC — which is the evening before for anybody west of Greenwich. A
 * day is therefore split by hand, so the date printed is the date sent
 * wherever the console happens to be open.
 *
 * Here rather than in each service because the roster and the dashboard print
 * the same dates from the same API, and a second copy of a format is a second
 * format.
 */
export const formatDate = (value: string | null | undefined): string => {
  if (!value) return "—";

  const day = DATE_ONLY.exec(value);
  const date = day
    ? new Date(Number(day[1]), Number(day[2]) - 1, Number(day[3]))
    : new Date(value);
  if (Number.isNaN(date.getTime())) return "—";

  return `${date.getDate()} ${date.toLocaleString("en-GB", {
    month: "short",
  })} ${date.getFullYear()}`;
};

/** How long ago, in the shortest form that is still unambiguous. Falls back to
 *  a plain date once "weeks ago" stops being easier to read than the day. */
export const formatSince = (value: string | null | undefined): string => {
  if (!value) return "—";
  const then = new Date(value);
  if (Number.isNaN(then.getTime())) return "—";

  const minutes = Math.floor((Date.now() - then.getTime()) / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;

  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;

  const weeks = Math.floor(days / 7);
  if (weeks < 5) return `${weeks}w ago`;

  return formatDate(value);
};

/**
 * "₹499" — rupees, and no paise where there are none.
 *
 * Naurist bills in whole rupees, so a trailing ".00" on every line of a
 * billing history is two characters of noise on every row. A figure that does
 * have paise keeps them.
 */
export const formatRupees = (amount: number): string =>
  `₹${amount.toLocaleString("en-IN", {
    minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
    maximumFractionDigits: 2,
  })}`;
